import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Calculator as CalculatorIcon, DollarSign, Calendar, TrendingUp, AlertCircle } from 'lucide-react';
import { loanTerms } from '../mockData';

export const Calculator = () => {
  const [loanAmount, setLoanAmount] = useState(5000);
  const [includeOpeningFee, setIncludeOpeningFee] = useState(true);
  const [calculation, setCalculation] = useState(null);

  useEffect(() => {
    calculateLoan();
  }, [loanAmount, includeOpeningFee]);

  const calculateLoan = () => {
    const openingFeeAmount = includeOpeningFee ? (loanAmount * loanTerms.openingFee) / 100 : 0;
    const totalWithFee = loanAmount + openingFeeAmount;
    const interestAmount = (totalWithFee * loanTerms.interestRate) / 100;
    const totalToPay = totalWithFee + interestAmount;
    const weeklyPayment = totalToPay / loanTerms.weeks;

    setCalculation({
      loanAmount,
      openingFee: openingFeeAmount,
      subtotalWithFee: totalWithFee,
      interest: interestAmount,
      totalToPay,
      weeklyPayment,
      weeks: loanTerms.weeks
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="p-6 md:p-8 shadow-xl border-2 border-amber-100">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-emerald-400 rounded-2xl mb-4">
            <CalculatorIcon className="text-white" size={32} />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Calculadora de Préstamo</h2>
          <p className="text-slate-600">Calcula tu préstamo y pagos semanales</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div>
              <Label htmlFor="loanAmount" className="text-lg font-semibold mb-3 block">
                Monto del Préstamo
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  id="loanAmount"
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  min="1000"
                  max="50000"
                  step="500"
                  className="pl-10 text-lg h-12"
                />
              </div>
              <input
                type="range"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                min="1000"
                max="50000"
                step="500"
                className="w-full mt-4 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider-thumb"
              />
              <div className="flex justify-between text-sm text-slate-500 mt-2">
                <span>$1,000</span>
                <span>$50,000</span>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg">
              <input
                type="checkbox"
                id="openingFee"
                checked={includeOpeningFee}
                onChange={(e) => setIncludeOpeningFee(e.target.checked)}
                className="w-5 h-5 accent-amber-500"
              />
              <Label htmlFor="openingFee" className="cursor-pointer flex-1">
                <span className="font-medium">Incluir cuota de apertura (5%)</span>
                <p className="text-sm text-slate-600">Solo para primer crédito</p>
              </Label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Información importante:</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• Tasa de interés: {loanTerms.interestRate}%</li>
                  <li>• Plazo: {loanTerms.weeks} semanas</li>
                  <li>• Pagos semanales</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-4">
            {calculation && (
              <>
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="text-slate-600" size={20} />
                    <span className="text-sm font-medium text-slate-600">Monto Solicitado</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculation.loanAmount)}</p>
                </div>

                {calculation.openingFee > 0 && (
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-5 border border-amber-200">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="text-amber-600" size={20} />
                      <span className="text-sm font-medium text-amber-700">Cuota de Apertura (5%)</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-900">{formatCurrency(calculation.openingFee)}</p>
                  </div>
                )}

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="text-blue-600" size={20} />
                    <span className="text-sm font-medium text-blue-700">Intereses ({loanTerms.interestRate}%)</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{formatCurrency(calculation.interest)}</p>
                </div>

                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="text-emerald-600" size={20} />
                    <span className="text-sm font-medium text-emerald-700">Pago Semanal</span>
                  </div>
                  <p className="text-3xl font-bold text-emerald-900">{formatCurrency(calculation.weeklyPayment)}</p>
                  <p className="text-sm text-emerald-700 mt-2">Durante {calculation.weeks} semanas</p>
                </div>

                <div className="bg-gradient-to-br from-amber-400 to-emerald-400 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="text-white" size={24} />
                    <span className="text-sm font-semibold">Total a Pagar</span>
                  </div>
                  <p className="text-4xl font-bold">{formatCurrency(calculation.totalToPay)}</p>
                </div>

                <a href="https://wa.me/5213329698523?text=Hola,%20quiero%20solicitar%20un%20crédito" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold h-12 text-lg shadow-lg">
                    Solicitar Este Préstamo
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
